import { APP_DETAILS, APP_KEY_BASE_PATHS, APP_ROUTE_BASE_PATHS } from "@dashboard/constants/appRegistry"
import { useIsOnline } from "@dashboard/features/online"
import { isOfflineReady } from "@dashboard/lib/offlineReady"
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { isAxiosError } from "axios"
import { profileServices, selectedAppStore } from "@workspace/shared"
import { SESSION_CLOSED_NOTICE_KEY, closedNoticeOnDashboard } from "@workspace/ui/hooks"
import { AppTile } from "./app-tile"
import { RequestAccessModal } from "./request-access-modal"
import { SeatsFullModal } from "./seats-full-modal"
import {
  DASHBOARD_TILES,
  DashboardTileDef,
  TileState,
  sessionHolders,
  sessionKey,
  tileState,
  tileTapAction,
} from "./tiles"
import type { DashboardGridProps } from "./types"

export type { AppCard, SessionsMap } from "./types"

/** Why this device is back on the dashboard, if a teammate closed its seat. */
function readClosedNotice(): string | null {
  try {
    const app = sessionStorage.getItem(SESSION_CLOSED_NOTICE_KEY)
    return app ? closedNoticeOnDashboard(app) : null
  } catch {
    return null
  }
}

/** The API's own words for a failed request, when it sent some. */
function requestErrorMessage(error: unknown, fallback: string): string {
  const message = isAxiosError(error) ? (error.response?.data as { message?: unknown } | undefined)?.message : null
  return typeof message === "string" && message ? message : fallback
}

/**
 * The dashboard's products (client 2026-11-04 mockups, as in-game): every
 * practice app as the sidebar's icon, whatever the subscriptions —
 *  - an app the team has opens on a tap;
 *  - one it has no subscription for is grey, and a tap offers "Request
 *    Access" (an email to sales);
 *  - one whose seats the team has all taken carries a lock and lists who is
 *    on; a tap offers to close the other sessions so this device can open it.
 */
const DashboardGrid: FC<DashboardGridProps> = ({ appsBySubscription, currentSessions, onSessionsRefresh }) => {
  const { isOnline } = useIsOnline()
  const navigate = useNavigate()
  /** A one-line answer to a tap that opens nothing (offline). */
  const [notice, setNotice] = useState<string | null>(null)
  /** "Your Linebacker session was closed from another device", once. */
  const [closedNotice, setClosedNotice] = useState<string | null>(readClosedNotice)
  const [requestTile, setRequestTile] = useState<DashboardTileDef | null>(null)
  const [lockedTile, setLockedTile] = useState<DashboardTileDef | null>(null)
  const [revokeLoading, setRevokeLoading] = useState(false)
  const [revokeError, setRevokeError] = useState<string | null>(null)

  // Shown once: the next visit to the dashboard starts clean. Removed here,
  // not where it is read, since a strict-mode double read must find it twice.
  useEffect(() => {
    try {
      sessionStorage.removeItem(SESSION_CLOSED_NOTICE_KEY)
    } catch {
      // Private mode: nothing was stored.
    }
  }, [])

  // Back online (offline → online, not on load): "connect to the internet"
  // no longer applies.
  const wasOnline = useRef(isOnline)
  useEffect(() => {
    if (isOnline && !wasOnline.current) setNotice(null)
    wasOnline.current = isOnline
  }, [isOnline])

  const tiles = useMemo(
    () =>
      DASHBOARD_TILES.map((tile) => {
        const session = currentSessions[sessionKey(tile.appId)]
        const state: TileState = tileState({
          subscribed: Boolean(appsBySubscription[tile.appId]),
          online: isOnline,
          // Opened online once on this device: its settings and plays are here.
          offlineReady: Boolean(APP_DETAILS[tile.appId].isOfflineCapable) && isOfflineReady(tile.appId),
          session,
        })
        return { tile, state, session, holders: sessionHolders(session?.values ?? []) }
      }),
    [appsBySubscription, currentSessions, isOnline],
  )

  const openApp = (tile: DashboardTileDef) => {
    const { setSelectedAppState } = selectedAppStore.getState()
    const appRoute = APP_ROUTE_BASE_PATHS[tile.appId]
    if (!appRoute) {
      console.warn(`No route configured for app: ${tile.appId}`)
      return
    }
    setSelectedAppState({ key: "appKey", value: APP_KEY_BASE_PATHS[tile.appId] })
    setNotice(null)
    navigate(appRoute)
  }

  const handleTileClick = (tile: DashboardTileDef, state: TileState) => {
    setClosedNotice(null)
    const action = tileTapAction({ state, online: isOnline })
    switch (action.kind) {
      case "open":
        return openApp(tile)
      case "request":
        setNotice(null)
        return setRequestTile(tile)
      case "seats":
        setNotice(null)
        setRevokeError(null)
        return setLockedTile(tile)
      case "notice":
        return setNotice(action.text)
    }
  }

  const closeLocked = useCallback(() => {
    if (revokeLoading) return
    setLockedTile(null)
    setRevokeError(null)
  }, [revokeLoading])

  // Closes every other device's seat in the app — teammates' and the
  // user's own elsewhere — so this one can take a seat (client 2026-11-04,
  // as in-game). Nobody is signed out: those devices go back to the
  // dashboard.
  const closeOtherSessions = useCallback(async () => {
    if (!lockedTile) return
    setRevokeLoading(true)
    setRevokeError(null)
    try {
      await profileServices.onCloseOtherSessions(lockedTile.product)
      // No close here: with the seats free the dialog closes itself (the
      // effect below), and the tile opens; if someone took a seat in the
      // meantime it stays open and says who.
      await onSessionsRefresh()
    } catch (error: unknown) {
      setRevokeError(
        requestErrorMessage(error, "Couldn't close the other sessions. Check your connection and try again."),
      )
    } finally {
      setRevokeLoading(false)
    }
  }, [lockedTile, onSessionsRefresh])

  // Only while the app is still full: a seat freed elsewhere (the live
  // refresh) closes the dialog, and the tile simply opens.
  const locked = lockedTile
    ? tiles.find((entry) => entry.tile.appId === lockedTile.appId && entry.state === "locked")
    : undefined
  useEffect(() => {
    if (lockedTile && !locked && !revokeLoading) setLockedTile(null)
  }, [locked, lockedTile, revokeLoading])

  useEffect(() => {
    const { setSelectedAppState } = selectedAppStore.getState()
    setSelectedAppState({ key: "appKey", value: undefined })
  }, [])

  return (
    <div>
      {closedNotice && !notice && (
        <p role="status" className="mx-auto mb-4 max-w-[296px] text-center text-sm text-[#374151] md:max-w-[360px]">
          {closedNotice}
        </p>
      )}
      {notice && (
        <p role="status" className="mx-auto mb-4 max-w-[296px] text-center text-sm text-red-600 md:max-w-[360px]">
          {notice}
        </p>
      )}
      {/* Two across on a phone, five on an iPad in landscape — the mockups. */}
      <div className="mx-auto grid w-fit grid-cols-2 items-start gap-x-2 gap-y-8 sm:grid-cols-3 md:grid-cols-4 md:gap-y-10 lg:grid-cols-5">
        {tiles.map(({ tile, state, holders }) => (
          <AppTile key={tile.appId} tile={tile} state={state} holders={holders} onClick={() => handleTileClick(tile, state)} />
        ))}
      </div>
      <RequestAccessModal key={requestTile?.appId ?? "none"} tile={requestTile} onClose={() => setRequestTile(null)} />
      {locked && (
        <SeatsFullModal
          tile={locked.tile}
          holders={locked.holders}
          limit={locked.session?.limit}
          loading={revokeLoading}
          error={revokeError}
          onCloseOthers={() => void closeOtherSessions()}
          onClose={closeLocked}
        />
      )}
    </div>
  )
}

export default DashboardGrid
